<template>
  <!-- 澄清 / 二次确认浮窗：接管输入区（对齐 Cursor / Qoder 的"等你选"交互）。
       三种形态自动切换：
         ① 选项带父子关系 → 父子树 + 搜索（复用 ChatWelcome 的 el-tree 范式）
         ② 无层级但 >8 项 → 列表 + 搜索
         ③ 无层级且 ≤8 项 → 按钮组
       点选后原地变「已选择」确认条，不直接消失——用户可确认或重新选择。 -->
  <transition name="clarify-pop">
    <div v-if="clarify" class="clarify-card" @click.stop>
      <div class="clarify-hd">
        <span class="clarify-ic">✦</span>
        <span class="clarify-t">{{ clarify.title || '请选择' }}</span>
        <span class="clarify-x" title="放弃本次选择" @click="$emit('dismiss')">✕ 放弃</span>
      </div>
      <div v-if="clarify.detail" class="clarify-detail">{{ clarify.detail }}</div>

      <!-- 已选择：确认条（点选后不消失，可重新选择） -->
      <div v-if="chosen" class="clarify-chosen">
        <span class="clarify-chosen-ic">✓</span>
        <span class="clarify-chosen-text">已选择：{{ chosenLabel }}</span>
        <button class="clarify-chosen-reset" @click="resetChoice">重新选择</button>
        <button class="clarify-chosen-go" :disabled="loading" @click="confirmChoice">继续</button>
      </div>

      <template v-else>
        <!-- ① 父子树 -->
        <div v-if="treeData" class="clarify-body">
          <el-input
            v-if="totalCount > 8"
            v-model="keyword"
            size="small"
            clearable
            placeholder="搜索塘口 / 基地名称"
            class="clarify-search"
          />
          <div class="clarify-tree-wrap">
            <el-tree
              ref="treeRef"
              :data="treeData"
              node-key="key"
              :props="treeProps"
              :filter-node-method="filterNode"
              :expand-on-click-node="false"
              :default-expanded-keys="expandedKeys"
              empty-text="没有匹配的选项"
              @node-click="onTreeNodeClick"
            >
              <template #default="{ node, data }">
                <div class="clarify-node" :class="{ 'is-group': data.isGroup, 'is-leaf': !data.isGroup }">
                  <span class="clarify-node-label">{{ data.label }}</span>
                  <span v-if="data.isGroup" class="clarify-node-count">{{ data.children.length }}</span>
                  <span v-else class="clarify-node-go">选择</span>
                </div>
              </template>
            </el-tree>
          </div>
          <div class="clarify-foot">
            共 {{ totalCount }} 项 · {{ groupCount }} 个上级
            <span v-if="keyword.trim()" class="clarify-foot-hit">· 匹配 {{ matchedCount }} 项</span>
          </div>
        </div>

        <!-- ② 列表 + 搜索 -->
        <div v-else-if="options.length > 8" class="clarify-body">
          <el-input
            v-model="keyword"
            size="small"
            clearable
            placeholder="搜索选项"
            class="clarify-search"
          />
          <div class="clarify-list">
            <button
              v-for="(opt, oi) in filteredOptions"
              :key="oi"
              class="clarify-opt"
              :disabled="loading"
              @click="pick(opt)"
            >
              <span class="clarify-opt-index">{{ indexLabel(oi) }}</span>
              <span class="clarify-opt-label">{{ opt.label || opt.value }}</span>
            </button>
            <div v-if="filteredOptions.length === 0" class="clarify-empty">没有匹配的选项，可直接在下方输入</div>
          </div>
          <div class="clarify-foot">共 {{ options.length }} 项</div>
        </div>

        <!-- ③ 按钮组 -->
        <div v-else-if="options.length" class="clarify-opts">
          <button
            v-for="(opt, oi) in options"
            :key="oi"
            class="clarify-opt"
            :disabled="loading"
            @click="pick(opt)"
          >
            <span class="clarify-opt-index">{{ indexLabel(oi) }}</span>
            <span class="clarify-opt-label">{{ opt.label || opt.value }}</span>
          </button>
        </div>

        <!-- 自由输入：任何形态都可用 -->
        <div class="clarify-input-row">
          <el-input
            v-model="freeText"
            size="default"
            :placeholder="options.length ? '都不合适？直接描述你的选择…' : '输入补充说明…'"
            :disabled="loading"
            @keyup.enter="submitFree"
          />
          <el-button type="primary" :disabled="loading || !freeText.trim()" @click="submitFree">发送</el-button>
        </div>
      </template>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

const props = defineProps({
  /** 澄清对象：{ key, title, detail, options:[{label,value,placeId,parentId,parentName,placeName}] } */
  clarify: { type: Object, default: null },
  loading: { type: Boolean, default: false }
})
const emit = defineEmits(['choose', 'submit', 'dismiss'])

const INDEX_CHARS = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳'
const indexLabel = (i) => INDEX_CHARS[i] || String(i + 1)
const treeProps = { children: 'children', label: 'label' }

const keyword = ref('')
const freeText = ref('')
const chosen = ref(null)
const treeRef = ref(null)

const options = computed(() => (props.clarify && Array.isArray(props.clarify.options) ? props.clarify.options : []))
const totalCount = computed(() => options.value.length)

/** 是否具备父子关系：有任一项带 parentId/parentName 即视为层级数据 */
const hasHierarchy = computed(() => options.value.some((o) => o && (o.parentId || o.parentName)))

/**
 * 组装两层树：上级（parentName）作分组节点，场地作叶子。
 * 没有上级的场地作为根叶子直接出现——不能因为"高一级在别处"就把数据藏起来。
 */
const treeData = computed(() => {
  if (!hasHierarchy.value || options.value.length <= 1) return null
  const groups = new Map()
  const roots = []
  for (const opt of options.value) {
    const pid = opt.parentId
    const pname = (opt.parentName || '').trim()
    if (!pid || !pname) {
      roots.push({ key: 'p' + (opt.placeId ?? opt.value), label: opt.label || opt.value, opt })
      continue
    }
    const gk = 'g' + pid
    if (!groups.has(gk)) {
      groups.set(gk, { key: gk, label: pname, isGroup: true, children: [] })
    }
    groups.get(gk).children.push({
      key: 'p' + (opt.placeId ?? opt.value),
      label: (opt.placeName || opt.label || opt.value || '').trim(),
      opt
    })
  }
  const tree = [...groups.values(), ...roots]
  // 只有一个分组且没有根叶子时，树化没有意义，退回列表/按钮
  if (tree.length <= 1 && !tree[0]?.isGroup) return null
  return tree
})

const groupCount = computed(() => (treeData.value ? treeData.value.filter((n) => n.isGroup).length : 0))

/** 叶子数量（搜索命中统计用） */
const leafCount = computed(() => {
  if (!treeData.value) return options.value.length
  return treeData.value.reduce((n, g) => n + (g.isGroup ? g.children.length : 1), 0)
})

const matchedCount = computed(() => {
  const kw = keyword.value.trim()
  if (!kw) return leafCount.value
  const lower = kw.toLowerCase()
  return options.value.filter((o) => String(o.label || o.value || '').toLowerCase().includes(lower)).length
})

const filteredOptions = computed(() => {
  const kw = keyword.value.trim().toLowerCase()
  if (!kw) return options.value
  return options.value.filter((o) => String(o.label || o.value || '').toLowerCase().includes(kw))
})

const expandedKeys = computed(() => (treeData.value || []).filter((n) => n.isGroup).map((n) => n.key))

const chosenLabel = computed(() => {
  const c = chosen.value
  if (!c) return ''
  return c.label || c.value || ''
})

/** el-tree 过滤：命中叶子保留；分组自身命中则保留整组子项 */
function filterNode(value, data) {
  if (!value) return true
  const kw = value.toLowerCase()
  if (data.isGroup) {
    if (String(data.label || '').toLowerCase().includes(kw)) return true
    return (data.children || []).some((c) => String(c.label || '').toLowerCase().includes(kw))
  }
  return String(data.label || '').toLowerCase().includes(kw)
}

watch(keyword, (v) => {
  if (treeRef.value) treeRef.value.filter(v)
})

/** 切换澄清对象时重置本地状态 */
watch(
  () => (props.clarify ? props.clarify.key : null),
  () => {
    keyword.value = ''
    freeText.value = ''
    chosen.value = null
    nextTick(() => {
      if (treeRef.value) treeRef.value.filter('')
    })
  }
)

function onTreeNodeClick(data) {
  if (!data || data.isGroup) return
  if (data.opt) pick(data.opt)
}

function pick(opt) {
  if (props.loading || !opt) return
  chosen.value = opt
}

function resetChoice() {
  chosen.value = null
}

/** 确认选择：把完整 option（含 placeId/parentId/parentName）交给父级，由其决定是否带结构化字段 */
function confirmChoice() {
  if (!chosen.value) return
  emit('choose', chosen.value)
}

function submitFree() {
  const text = freeText.value.trim()
  if (!text || props.loading) return
  freeText.value = ''
  emit('submit', text)
}
</script>

<style scoped>
.clarify-card {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  padding: 18px 22px 16px;
  background: #fff;
  border-top: 1px solid #ebeef5;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 -10px 36px rgba(31, 45, 61, 0.16);
  display: flex;
  flex-direction: column;
  max-height: 68vh;
}

.clarify-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #7c3aed, #4f8cff);
}

.clarify-hd {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.clarify-ic {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: linear-gradient(135deg, #7c3aed, #4f8cff);
  color: #fff;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.clarify-t {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  line-height: 1.6;
  flex: 1;
  min-width: 0;
}

.clarify-x {
  font-size: 12.5px;
  color: #909399;
  cursor: pointer;
  flex-shrink: 0;
  padding: 3px 7px;
  border-radius: 5px;
}

.clarify-x:hover {
  color: #f56c6c;
  background: #fef0f0;
}

.clarify-detail {
  margin: 0 0 10px 33px;
  font-size: 12.5px;
  color: #909399;
  line-height: 1.7;
  flex-shrink: 0;
}

.clarify-body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-left: 33px;
}

.clarify-search {
  margin-bottom: 8px;
  flex-shrink: 0;
}

.clarify-tree-wrap {
  overflow-y: auto;
  max-height: 300px;
  border: 1px solid #ebeef5;
  border-radius: 9px;
  padding: 6px 4px;
}

.clarify-node {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  font-size: 13.5px;
}

.clarify-node.is-group {
  color: #303133;
  font-weight: 600;
}

.clarify-node-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clarify-node-count {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
  background: #f0f2f5;
  border-radius: 9px;
  padding: 0 7px;
}

.clarify-node-go {
  font-size: 12px;
  color: #409eff;
  opacity: 0;
  transition: opacity 0.15s;
}

.clarify-node.is-leaf:hover .clarify-node-go {
  opacity: 1;
}

.clarify-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;
  overflow-y: auto;
  max-height: 264px;
  padding-right: 4px;
}

.clarify-opts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
  margin: 8px 0 4px 33px;
  max-height: 264px;
  overflow-y: auto;
  padding-right: 4px;
}

.clarify-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  font-size: 13.5px;
  padding: 10px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 9px;
  background: #fff;
  color: #303133;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1.5;
}

.clarify-opt-index {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f0f2f5;
  color: #606266;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}

.clarify-opt-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clarify-opt:hover:not(:disabled) {
  border-color: #409eff;
  background: #ecf5ff;
}

.clarify-opt:hover:not(:disabled) .clarify-opt-index {
  background: #409eff;
  color: #fff;
}

.clarify-opt:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.clarify-empty {
  font-size: 13px;
  color: #909399;
  padding: 12px 4px;
}

.clarify-foot {
  margin-top: 8px;
  font-size: 12px;
  color: #a8abb2;
  flex-shrink: 0;
}

.clarify-foot-hit {
  color: #409eff;
}

.clarify-chosen {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 4px 33px;
  padding: 10px 14px;
  border: 1px solid #b3e19d;
  background: #f0f9eb;
  border-radius: 9px;
  font-size: 13.5px;
  color: #303133;
}

.clarify-chosen-ic {
  color: #67c23a;
  font-size: 14px;
}

.clarify-chosen-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clarify-chosen-reset {
  font-size: 12.5px;
  color: #909399;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 5px;
}

.clarify-chosen-reset:hover {
  color: #409eff;
  background: #ecf5ff;
}

.clarify-chosen-go {
  font-size: 12.5px;
  color: #fff;
  background: #409eff;
  border: none;
  border-radius: 6px;
  padding: 5px 14px;
  cursor: pointer;
}

.clarify-chosen-go:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.clarify-input-row {
  display: flex;
  gap: 10px;
  margin: 12px 0 0 33px;
  flex-shrink: 0;
}

.clarify-input-row .el-input {
  flex: 1;
}

.clarify-pop-enter-active,
.clarify-pop-leave-active {
  transition: all 0.26s cubic-bezier(0.22, 1, 0.36, 1);
}

.clarify-pop-enter-from,
.clarify-pop-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}
</style>
